import { createContext, useContext, useState, useSyncExternalStore, type ReactNode } from "react";
import { WorkDataStore, type WorkMutation } from "./workDataStore";
import type { WorkMutationResult } from "./types";
import { Button } from "./components/ui/Button";
import { Feedback } from "./components/ui/Surface";
import { Modal } from "./components/ui/Modal";

export const WorkDataContext = createContext<WorkDataStore | null>(null);
export function WorkDataProvider({ store, children }: { store: WorkDataStore; children: ReactNode }) {
  const state = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  const [dismissed, setDismissed] = useState<string | null>(null);
  const unresolved = state.pending.find(value => value.status === "uncertain");
  return <WorkDataContext.Provider value={store}>
    {state.pending.filter(value => value.status === "uncertain").map(value => <Feedback key={value.key} tone="info">
      {value.label} · 저장 결과 확인 필요 <Button size="compact" onClick={() => { void store.retry(value.key)?.catch(() => {}); }}>저장 결과 확인</Button>
    </Feedback>)}
    {children}
    {unresolved && unresolved.key !== dismissed && <Modal title="저장 결과 확인" onClose={() => setDismissed(unresolved.key)}>
      <Feedback tone="info">{unresolved.label}의 저장 결과를 확인하지 못했습니다.</Feedback>
      <Button variant="primary" onClick={() => { void store.retry(unresolved.key)?.catch(() => {}); }}>저장 결과 확인</Button>
    </Modal>}
  </WorkDataContext.Provider>;
}

export function useWorkMutation(refresh: () => Promise<void>) {
  const store = useContext(WorkDataContext);
  return <T extends WorkMutationResult>(mutation: WorkMutation<T>): Promise<T> => {
    if (store) return store.execute(mutation, refresh);
    // Isolated components retain their previous API without treating a refresh
    // error as a failed save; the app always supplies the shared store.
    return mutation.request().then(result => {
      mutation.onSuccess?.(result);
      void refresh().catch(() => {});
      return result;
    });
  };
}

export function useWorkDataState() {
  const store = useContext(WorkDataContext);
  const state = useSyncExternalStore(store?.subscribe ?? emptySubscribe, store?.getSnapshot ?? emptySnapshot, store?.getSnapshot ?? emptySnapshot);
  return state;
}
export function usePendingWork() { return useWorkDataState().pending; }
const emptySubscribe = () => () => {};
const empty = { data: null, pending: [], version: 0, recordVersions: new Map<string, number>() };
const emptySnapshot = () => empty;
